import { useQuery } from "@tanstack/react-query";
import { retryUnlessRefused } from "@/lib/api-client";
import {
  getCallList,
  getCallDetails,
  getRecordingLink,
  getWorkspaceConcurrency,
  CallLogFilters,
} from "./call.service";

// List of calls for the Call History table (lean columns, paginated, filtered).
export const useCallList = (
  workspaceId?: string,
  page: number = 1,
  pageSize: number = 100,
  filters?: CallLogFilters
) =>
  useQuery({
    queryKey: ["call-logs", workspaceId, page, pageSize, filters],
    queryFn: () => getCallList({ workspaceId: workspaceId!, page, pageSize, filters }),
    enabled: !!workspaceId,
  });

// Full details for a single call — fetched on demand when the dialog opens.
export const useCallDetails = (id?: string, enabled: boolean = true) =>
  useQuery({
    queryKey: ["call-detail", id],
    queryFn: () => getCallDetails(id!),
    enabled: !!id && enabled,
  });

// Live workspace concurrency (active calls vs the enforced cap) for the
// dashboard tile. Polled every 5s: realtime isn't wired for call_logs, and the
// rest of the app standardizes on refetchInterval polling (see campaign.query).
export const useWorkspaceConcurrency = (workspaceId: string | undefined) =>
  useQuery({
    queryKey: ["workspace-concurrency", workspaceId],
    queryFn: () => getWorkspaceConcurrency(workspaceId!),
    enabled: !!workspaceId,
    refetchInterval: 5000,
  });

// The link a Twilio recording plays from: one each time the recording tab opens. Never re-read while the tab
// stays open — a new link would restart the player mid-listen — and dropped when it closes, so the next opening
// starts a fresh hour. A refusal (no such recording, not the caller's call) is final.
export const useRecordingLink = (id: string) =>
  useQuery({
    queryKey: ["recording-link", id],
    queryFn: () => getRecordingLink(id),
    staleTime: Infinity,
    gcTime: 0,
    retry: retryUnlessRefused,
  });
